<template>
  <GamMainView class="gambits-view" :class="{ 'is-mobile': isMobile }">
    <ResultListView>
      <GamListView
        v-if="isLocationValid"
        v-bind="getGambitList"
        show-footer
        is-horizontally-padded
        :key="`refreshed-at-${lastRefreshedAt}`"
      >
        <template #header>
          <GambitsListHeader />
          <Vue3PullToRefresh
            :distance="62"
            :duration="300"
            :size="32"
            noreload
            :options="{ color: '#CCC', bgColor: '#282828' }"
            @onrefresh="handleRefreshList"
          />
        </template>

        <template #list>
          <template v-if="augmentedItemList?.length">
            <GamGambitResultCard
              v-for="gambit in augmentedItemList"
              :gambit="gambit"
              :location="gambit.location || null"
              :key="gambit.id"
              :selected="route.params.id === gambit.id"
              @click="navigateToGambit(gambit.id)"
            />
          </template>
          <template v-else-if="isListLoading">
            <div class="skeleton-loader">
              <div class="skeleton-item skeleton o-2" />
              <div class="skeleton-item skeleton o-3" />
              <div class="skeleton-item skeleton o-4" />
            </div>
          </template>
        </template>

        <template #footer>
          <MapCoordsButton v-if="!filterStore.getActiveTab() && (!isMobile || !route.params.id)" />
          <span v-else />
        </template>
      </GamListView>
    </ResultListView>

    <GambitOverlay
      v-if="gambitDetails && route.params.id"
      :gambit="gambitDetails"
      :is-open="true"
      :key="String(route.params.id)"
      @close="handleOverlayClose"
    />
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType, TabItemType } from '@/core/data/config/uiConfig.interface';
import { GambitSparkState, type BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import { GambitCategory } from '@/core/data/gambit/gambit.type';
import { gambits, localize } from '@/core/gambits';
import { listInfo, nearbyListInfo } from '@/core/helpers/list.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import type { GetGambitTableFilters, GetTableParams } from '@/core/network/http/httpClient.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useGambitStore } from '@/stores/gambit.store';
import { useMapStore } from '@/stores/map.store';
import { useSparkStore } from '@/stores/spark.store';
import { useTableStore } from '@/stores/table.store';
import GamGambitResultCard from '@/views/components/gam-gambits/GamGambitResultCard.vue';
import GamListView from '@/views/components/gam-list-view/GamListView.vue';
import MapCoordsButton from '@/views/components/gam-location/MapCoordsButton.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import ResultListView from '@/views/components/ResultListView.vue';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import type { GamListType } from '@/views/composables/models/components/GamList';
// @ts-ignore
import Vue3PullToRefresh from '@amirafa/vue3-pull-to-refresh';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GambitOverlay from './GambitOverlay.vue';
import GambitsListHeader from './GambitsListHeader.vue';

const listId = GamListId.GAMBITS;
const tableStore = useTableStore<BaseGambitDto>(listId)();
const { itemList, isLoading: isListLoading, tableParams } = storeToRefs(tableStore);
const filterStore = useFilterStore<GetTableParams>(listId)();
const { listTabItems } = storeToRefs(filterStore);

const sparkStore = useSparkStore();
const { sparkGambit, gambitId: sparkGambitId, isGambitNotFound: isSparkGambitNotFound } = storeToRefs(sparkStore);

const gambitStore = useGambitStore();
const {
  gambit: gambitDetails,
  isLoading: isDetailLoading,
  isNotFound: isGambitNotFound,
  mergedIntoGambitId,
} = storeToRefs(gambitStore);

const mapStore = useMapStore();
const { currentLocation, currentZoom, isLocationValid } = storeToRefs(mapStore);

const route = useRoute();
const router = useRouter();

const lastRefreshedAt = ref<number>(new Date().getTime());

const augmentedItemList = computed(() => {
  return itemList.value
    ? itemList.value
        .filter((g) => g.gambitCategory !== GambitCategory.SPARK || g.sparkState !== GambitSparkState.ABANDONED)
        .map((g) => (g.id === sparkGambit.value?.id ? { ...g, ...sparkGambit, location: g.location } : g))
    : [];
});

const handleOverlayClose = () => {
  gambitStore.clear();
  router.push({ name: RouterNameType.GAMBITS, query: route.query });
};

const navigateToGambit = (id: string) => {
  const gambitRoute = !isMobile.value
    ? { name: RouterNameType.GAMBITS, params: { id }, query: route.query }
    : { name: RouterNameType.GAMBIT, params: { id }, query: { back: 'history' } };

  router.push(gambitRoute);
};

onBeforeMount(() => {
  filterStore.setFilters({
    onlyOneFutureRecurrence: true,
  } as GetGambitTableFilters);
});

watch(currentLocation, () => {
  if (!isLocationValid.value) return;
  if (!tableParams.value.beforeCursor) tableStore.fetchItems();
});

onUnmounted(() => {
  gambitStore.clear();
  tableStore.resetList(true);
});

const handleRefreshList = () => {
  lastRefreshedAt.value = new Date().getTime();
};

const nearbyListEndInfo = computed(() =>
  nearbyListInfo({
    id: listId,
    title:
      Array.isArray(itemList?.value) && itemList.value.length > 0
        ? 'No more Gambits nearby'
        : 'No upcoming Gambits nearby',
    currentLocation: currentLocation.value,
    currentZoom: currentZoom.value,
  }),
);

const noPersonalDataInfo = listInfo({
  id: listId,
  title: 'No upcoming Gambits',
  message: 'Gambits you join or organize will appear here for quick access.',
  label: 'Find a Gambit',
  onClick: () => {
    router.push({
      name: RouterNameType.LOCATIONS,
    });
  },
});

const noFollowingDataInfo = listInfo({
  id: listId,
  title: 'No upcoming Gambits',
  message: localize('gambits.list.following.empty.message'),
  label: 'Match a player',
  onClick: () => {
    router.push({
      name: RouterNameType.SPARKS,
    });
  },
});

const endInfo = computed(() => {
  return listTabItems.value?.[0].isActive === true ? nearbyListEndInfo.value : undefined;
});

const noDataInfo = computed(() => {
  if (!filterStore.getActiveTab()) {
    return nearbyListEndInfo.value;
  }

  if (filterStore.getActiveTab() === TabItemType.MINE) {
    return noPersonalDataInfo;
  }

  if (filterStore.getActiveTab() === TabItemType.FOLLOWING) {
    return noFollowingDataInfo;
  }

  return undefined;
});

const getGambitList = computed((): GamListType<BaseGambitDto> => {
  return {
    listId,
    endInfo: endInfo.value,
    noDataInfo: noDataInfo.value,
    scrollSpacing: true,
    isHorizontallyPadded: true,
    spacerPadding: isMobile.value ? 90 : 110,
    footerPadding: isMobile.value ? 78 : 42,
    loadCallback: (listId) => {
      return gambits.gambitService.getGambits(listId, currentLocation.value);
    },
  };
});

watch(
  () => [route.params.id, route.query.reload],
  async ([id, reload]) => {
    if (!id) {
      gambitStore.clear();
      return;
    }

    await gambitStore.fetch(String(id), currentLocation.value);

    if (reload === 'true') {
      nextTick(() => {
        router.replace({
          ...route,
          query: { ...route.query, reload: undefined },
        });
      });
    }
  },
  { immediate: true },
);

watch(mergedIntoGambitId, (survivingId) => {
  if (!survivingId) return;
  const previousId = String(route.params.id);
  mergedIntoGambitId.value = null;
  if (previousId) {
    tableStore.removeItem({ id: previousId });
  }
  router.replace({
    name: RouterNameType.GAMBITS,
    params: { id: survivingId },
    query: route.query,
  });
});

watch(
  () => [gambitDetails.value, isGambitNotFound.value, sparkGambit.value, isSparkGambitNotFound.value],
  ([gambit, isNotFound, sparkGambit, isSparkGambitNotFound]) => {
    if (mergedIntoGambitId.value) {
      return;
    }

    if (isNotFound) {
      const gambitId = gambit && typeof gambit !== 'boolean' ? gambit.id : String(route.params.id);
      tableStore.removeItem({ id: gambitId });
      router.replace({ name: RouterNameType.GAMBITS, query: route.query });
    }

    if (isSparkGambitNotFound && sparkGambitId.value) {
      tableStore.removeItem({ id: sparkGambitId.value });
    }

    const isGambitDefined = gambit && typeof gambit !== 'boolean';
    const isSparkGambitDefined = sparkGambit && typeof sparkGambit !== 'boolean';
    // const areBothDefined = isGambitDefined && isSparkGambitDefined;
    if (
      isGambitDefined &&
      gambit.isSpark &&
      !gambit.isConfirmed &&
      (!isSparkGambitDefined || gambit.id !== sparkGambit.id)
    ) {
      tableStore.removeItem({ id: gambit.id });
      // @ts-ignore
      router.push({ ...route, params: { ...route.params, id: undefined }, query: route.query });
    }

    if (gambit) {
      // TODO: Harmonize types
      // @ts-ignore
      tableStore.updateItem({ ...(gambit as GambitDetailDto), distance: undefined });
    }

    if (sparkGambit) {
      // TODO: Harmonize types
      // @ts-ignore
      tableStore.updateItem({ ...(sparkGambit as GambitDetailDto), distance: undefined });
    }
  },
);

// TODO: Just render this in augmentedItems list insted of
// adding the element to the itemList? We'll have to have it
// in the list for keyboard nav & swipe nav, but we should manage
// state in a store, not in the list view.

watch(
  () => ({
    isListLoading: isListLoading.value,
    isDetailLoading: isDetailLoading.value,
    gambits: itemList.value,
    gambitDetails: gambitDetails.value,
  }),
  ({ isListLoading, isDetailLoading, gambits, gambitDetails }) => {
    if (isListLoading || isDetailLoading || !gambitDetails) return;

    const existingGambit = gambits?.find((p) => p.id === gambitDetails?.id);
    if (!existingGambit) {
      nextTick(() => {
        tableStore.insertItem(gambitDetails as unknown as BaseGambitDto);
      });
    }
  },
);

watch(
  () => route.query.filter,
  (newFilter, prevFilter) => {
    if (!newFilter) {
      newFilter = 'mine';
    }

    if (newFilter === prevFilter) return;

    gambitStore.clear();
    filterStore.setActiveTab(String(newFilter) as TabItemType);
    tableStore.loadItems({ selectFirstItem: !isMobile.value });
  },
  { immediate: true },
);

watch(
  () => [route.params.id, itemList.value],
  async ([id, items]) => {
    if (!!id || !items?.length) return;

    if (!id && !isMobile.value) {
      id = (items as BaseGambitDto[])[0].id as string;
      nextTick(() => {
        router.replace({ name: RouterNameType.GAMBITS, params: { id: id as string }, query: route.query });
      });
    }
  },
);
</script>

<style scoped lang="scss">
.gambits-view.is-mobile {
  .gam-gambit-card {
    .gambit-base-info {
      max-width: calc(100svw - 58px);
    }
  }
}

.skeleton-loader {
  .skeleton-item {
    min-height: 130px !important;
  }
}

.gambits-view :deep(.gam-intersect .gam-intersect-wrapper .skeleton-loader) {
  gap: 16px;
}

.gambits-view :deep(.gam-list-view .gam-list-container .list-content) {
  gap: 16px;
}

.gambits-view :deep(.missing-info-container) {
  padding-bottom: calc(3 * var(--spacing-xxl) + var(--spacing-xl));
}
</style>
